import React, { useCallback, useRef, useState } from 'react';

/**
 * RedactorDrawOverlay – transparent canvas overlay that lets the user
 * click-and-drag to draw a black rectangle over an area of the preview.
 *
 * When the user finishes drawing, `onDraw` is called with the normalised
 * rectangle (x, y, width, height as 0-1 fractions of the container).
 *
 * Props:
 *   active   – boolean, whether drawing mode is enabled
 *   onDraw   – (rect: {x, y, width, height}) => void
 *   onCancel – () => void
 *   containerHeight – px height to match the preview
 */
function RedactorDrawOverlay({ active, onDraw, onCancel, mode = 'add' }) {
  const overlayRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [start, setStart] = useState(null);
  const [current, setCurrent] = useState(null);

  const getPos = useCallback((e) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
      containerW: rect.width,
      containerH: rect.height,
    };
  }, []);

  const handlePointerDown = (e) => {
    if (!active) return;
    e.preventDefault();
    const pos = getPos(e);
    setStart(pos);
    setCurrent(pos);
    setDrawing(true);
  };

  const handlePointerMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    setCurrent(getPos(e));
  };

  const handlePointerUp = (e) => {
    if (!drawing || !start || !current) return;
    e.preventDefault();
    setDrawing(false);

    const cw = start.containerW;
    const ch = start.containerH;
    const x1 = Math.min(start.x, current.x);
    const y1 = Math.min(start.y, current.y);
    const x2 = Math.max(start.x, current.x);
    const y2 = Math.max(start.y, current.y);

    const w = x2 - x1;
    const h = y2 - y1;

    // Ignore tiny accidental clicks (less than 10px either direction)
    if (w < 10 || h < 10) {
      setStart(null);
      setCurrent(null);
      return;
    }

    onDraw({
      x: x1 / cw,
      y: y1 / ch,
      width: w / cw,
      height: h / ch,
    });

    setStart(null);
    setCurrent(null);
  };

  // Compute the selection rect in px for the visual indicator
  let selRect = null;
  if (drawing && start && current) {
    const x1 = Math.min(start.x, current.x);
    const y1 = Math.min(start.y, current.y);
    selRect = {
      left: x1,
      top: y1,
      width: Math.abs(current.x - start.x),
      height: Math.abs(current.y - start.y),
    };
  }

  if (!active) return null;

  return (
    <div
      ref={overlayRef}
      onMouseDown={handlePointerDown}
      onMouseMove={handlePointerMove}
      onMouseUp={handlePointerUp}
      onTouchStart={handlePointerDown}
      onTouchMove={handlePointerMove}
      onTouchEnd={handlePointerUp}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10,
        cursor: 'crosshair',
        userSelect: 'none',
      }}
    >
      {/* Instruction banner */}
      <div
        className="d-flex justify-content-between align-items-center px-3 py-2"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          background: mode === 'remove' ? 'rgba(220,53,69,0.85)' : 'rgba(0,0,0,0.75)',
          color: '#fff',
          zIndex: 11,
          fontSize: 14,
        }}
      >
        <span>
          {mode === 'remove' ? (
            <><i className="bi bi-eraser me-2"></i>Draw a box to unredact that area</>
          ) : (
            <><i className="bi bi-pencil-square me-2"></i>Click and drag to draw a redaction box</>
          )}
        </span>
        <button
          className="btn btn-sm btn-outline-light"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onCancel();
          }}
        >
          Cancel
        </button>
      </div>

      {/* Selection rectangle */}
      {selRect && (
        <div
          style={{
            position: 'absolute',
            left: selRect.left,
            top: selRect.top,
            width: selRect.width,
            height: selRect.height,
            border: `2px dashed ${mode === 'remove' ? '#28a745' : '#dc3545'}`,
            background: mode === 'remove' ? 'rgba(40,167,69,0.15)' : 'rgba(0,0,0,0.3)',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}

export default RedactorDrawOverlay;
